import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import AttributePicker from "../Common/AttributePicker.tsx";
import { Environment as environment } from "../../environment.ts";
import { Type, FileText, ChevronDown, Info } from 'lucide-react';

interface Attribute {
    attributeId: string;
    attributeName: string;
    attributeType: 'TEXT' | 'NUMBER' | 'ARRAY' | 'OBJECT' | 'BOOLEAN';
}

const MultiProductDialog: React.FC<any> = ({ data, onClose }) => {
    const { nodes, setNodes } = useFlowStore();
    const { businessId } = useFlowStore();

    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [mode, setMode] = useState<'dynamic' | 'fixed'>('dynamic');
    const [fixedRows, setFixedRows] = useState<{ title: string; description: string }[]>([{ title: '', description: '' }]);
    const [arrayAttribute, setArrayAttribute] = useState("");
    const [titlePath, setTitlePath] = useState("");
    const [descPath, setDescPath] = useState("");
    const [header, setHeader] = useState("");
    const [body, setBody] = useState("");
    const [footer, setFooter] = useState("");
    const [buttonText, setButtonText] = useState("");
    const [saveAs, setSaveAs] = useState("");
    const [sampleJson, setSampleJson] = useState("");
    const [inferredPaths, setInferredPaths] = useState<string[]>([]);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    const flattenObject = (obj: any, prefix = ''): string[] => {
        let paths: string[] = [];
        if (typeof obj === 'object' && obj !== null) {
            for (const key in obj) {
                const newPrefix = prefix ? `${prefix}.${key}` : key;
                paths.push(newPrefix);
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    paths = paths.concat(flattenObject(obj[key], newPrefix));
                }
            }
        }
        return paths;
    };

    useEffect(() => {
        if (sampleJson) {
            try {
                const parsed = JSON.parse(sampleJson);
                setInferredPaths(flattenObject(Array.isArray(parsed) ? parsed[0] : parsed));
            } catch {
                setInferredPaths([]);
            }
        } else {
            setInferredPaths([]);
        }
    }, [sampleJson]);

    useEffect(() => {
        if (!businessId) return;
        axios.put(`${environment.baseUrl}/fetch/attribute`, {
            businessId, page: 0, size: 100, templateStatus: null, searchText: ''
        }).then(r => setAttributes(r.data.data.data || [])).catch(() => {});
    }, [businessId]);

    useEffect(() => {
        const node = nodes.find(n => n.data.id === data.nodeId);
        if (node) {
            const content = (node.data as CanvasNodeData).content.find(c => c.id === data.subNodeId);
            if (content?.data) {
                const d = content.data as any;
                setMode(d.mode || (d.fixedRows?.length ? 'fixed' : 'dynamic'));
                setFixedRows(d.fixedRows?.length ? d.fixedRows : [{ title: '', description: '' }]);
                setArrayAttribute(d.arrayAttribute || "");
                setTitlePath(d.titlePath || "");
                setDescPath(d.descPath || "");
                setHeader(d.header || "");
                setBody(d.body || d.text || "");
                setFooter(d.footer || "");
                setButtonText(d.buttonText || "");
                setSaveAs(d.saveAs || "");
                setSampleJson(d.sampleJson || "");
            }
        }
    }, []);

    const handleSave = () => {
        const updatedNodes = nodes.map(node => {
            if (node.data.id === data.nodeId) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        content: (node.data as CanvasNodeData).content.map(c => {
                            if (c.id === data.subNodeId) {
                                return {
                                    ...c,
                                    data: {
                                        mode, fixedRows, arrayAttribute, titlePath, descPath,
                                        header, body, footer, buttonText, saveAs, sampleJson
                                    }
                                };
                            }
                            return c;
                        })
                    }
                };
            }
            return node;
        });
        setNodes(updatedNodes as CanvasNode[]);
        onClose();
    };

    return (
        <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Mode toggle */}
            <div>
                <label className="label-premium">List Source <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button
                        style={{
                            flex: 1, padding: '7px 0', fontSize: '12px', borderRadius: '8px', border: '1px solid',
                            background: mode === 'dynamic' ? '#eff6ff' : 'transparent',
                            borderColor: mode === 'dynamic' ? '#60a5fa' : '#e2e8f0',
                            color: mode === 'dynamic' ? '#1d4ed8' : '#94a3b8',
                            fontWeight: mode === 'dynamic' ? 600 : 400, cursor: 'pointer'
                        }}
                        onClick={() => setMode('dynamic')}
                    >Dynamic (from attribute)</button>
                    <button
                        style={{
                            flex: 1, padding: '7px 0', fontSize: '12px', borderRadius: '8px', border: '1px solid',
                            background: mode === 'fixed' ? '#f0fdf4' : 'transparent',
                            borderColor: mode === 'fixed' ? 'var(--primary-bg-color)' : '#e2e8f0',
                            color: mode === 'fixed' ? 'var(--primary-bg-color)' : '#94a3b8',
                            fontWeight: mode === 'fixed' ? 600 : 400, cursor: 'pointer'
                        }}
                        onClick={() => setMode('fixed')}
                    >Fixed items</button>
                </div>
            </div>

            {/* Dynamic: array attribute select */}
            {mode === 'dynamic' && (
                <div>
                    <label className="label-premium">Array Attribute <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                        className="premium-input"
                        style={{ marginTop: '4px' }}
                        value={arrayAttribute}
                        onChange={e => setArrayAttribute(e.target.value)}
                    >
                        <option value="">Select Array Attribute</option>
                        {attributes.filter(a => a.attributeType?.toUpperCase() === 'ARRAY').map(a => (
                            <option key={a.attributeId} value={a.attributeName}>{a.attributeName}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Fixed: row editor */}
            {mode === 'fixed' && (
                <div>
                    <label className="label-premium">List Items <span style={{ color: '#ef4444' }}>*</span></label>
                    {fixedRows.map((row, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '4px' }}>
                                <input
                                    className="premium-input"
                                    placeholder={`Title ${i + 1}`}
                                    maxLength={24}
                                    value={row.title}
                                    onChange={e => {
                                        const r = [...fixedRows]; r[i] = { ...r[i], title: e.target.value }; setFixedRows(r);
                                    }}
                                />
                                <input
                                    className="premium-input"
                                    placeholder="Description (optional)"
                                    maxLength={72}
                                    value={row.description}
                                    onChange={e => {
                                        const r = [...fixedRows]; r[i] = { ...r[i], description: e.target.value }; setFixedRows(r);
                                    }}
                                />
                            </div>
                            {fixedRows.length > 1 && (
                                <button
                                    style={{ color: '#f87171', marginTop: '6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}
                                    onClick={() => setFixedRows(fixedRows.filter((_, j) => j !== i))}
                                >✕</button>
                            )}
                        </div>
                    ))}
                    {fixedRows.length < 10 && (
                        <button
                            style={{ marginTop: '8px', fontSize: '12px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}
                            onClick={() => setFixedRows([...fixedRows, { title: '', description: '' }])}
                        >+ Add item</button>
                    )}
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>{fixedRows.length}/10 items</div>
                </div>
            )}

            {/* Header */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="label-premium">Header</label>
                    <VariablePicker onSelect={val => setHeader(prev => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" placeholder="e.g. Current Gold Rates" value={header} onChange={e => setHeader(e.target.value)} />
            </div>

            {/* Body */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="label-premium">Body Text <span style={{ color: '#ef4444' }}>*</span></label>
                    <VariablePicker onSelect={val => setBody(prev => prev + `{{${val}}}`)} />
                </div>
                <textarea className="premium-input" style={{ minHeight: '60px' }} placeholder="Briefly describe the collection..." value={body} onChange={e => setBody(e.target.value)} />
            </div>

            {/* Footer */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="label-premium">Footer</label>
                    <VariablePicker onSelect={val => setFooter(prev => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" placeholder="e.g. Choose any to see more" value={footer} onChange={e => setFooter(e.target.value)} />
            </div>

            {/* Button text */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="label-premium">List Button Text <span style={{ color: '#ef4444' }}>*</span></label>
                    <VariablePicker onSelect={val => setButtonText(prev => prev + `{{${val}}}`)} />
                </div>
                <input className="premium-input" placeholder="e.g. View Catalog" value={buttonText} maxLength={20} onChange={e => setButtonText(e.target.value)} />
                <div style={{ textAlign: 'right', fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>{buttonText.length}/20</div>
            </div>

            {/* Save as */}
            <div>
                <label className="label-premium">Save selection as</label>
                <AttributePicker value={saveAs} onChange={setSaveAs} placeholder="— Save to attribute (optional) —" defaultType="OBJECT" />
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                    Stores the chosen item under this attribute so later steps can use it via {'{{'}name{'}}'}. Leave blank to only use SELECTED_ITEM.
                </div>
            </div>

            {/* Data mapping (dynamic only) */}
            {mode === 'dynamic' && <>
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0' }} />
                <div>
                    <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Info size={12} style={{ color: '#3b82f6' }} /> Data Mapping Assistant
                    </label>
                    <div style={{ fontSize: '10px', color: '#64748b', margin: '4px 0 8px' }}>Paste a sample item from your API to see available paths.</div>
                    <textarea
                        className="premium-input"
                        style={{ fontFamily: 'monospace', fontSize: '10px', background: '#f8fafc', borderStyle: 'dashed' }}
                        placeholder='e.g. { "name": "Gold 101", "brief": "10% off" }'
                        value={sampleJson}
                        onChange={e => setSampleJson(e.target.value)}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ position: 'relative' }}>
                        <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Type size={12} /> Title Path</label>
                        <div style={{ position: 'relative' }}>
                            <input className="premium-input" style={{ paddingRight: '28px' }} placeholder="e.g. name" value={titlePath} onChange={e => setTitlePath(e.target.value)} />
                            {inferredPaths.length > 0 && (
                                <button style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setActiveDropdown(activeDropdown === 'title' ? null : 'title')}>
                                    <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                        {activeDropdown === 'title' && (
                            <div className="path-menu shadow-xl border border-slate-200">
                                {inferredPaths.map(p => <div key={p} className="path-menu-item" onClick={() => { setTitlePath(p); setActiveDropdown(null); }}>{p}</div>)}
                            </div>
                        )}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <label className="label-premium" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FileText size={12} /> Description</label>
                        <div style={{ position: 'relative' }}>
                            <input className="premium-input" style={{ paddingRight: '28px' }} placeholder="e.g. description" value={descPath} onChange={e => setDescPath(e.target.value)} />
                            {inferredPaths.length > 0 && (
                                <button style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setActiveDropdown(activeDropdown === 'desc' ? null : 'desc')}>
                                    <ChevronDown size={14} />
                                </button>
                            )}
                        </div>
                        {activeDropdown === 'desc' && (
                            <div className="path-menu shadow-xl border border-slate-200">
                                {inferredPaths.map(p => <div key={p} className="path-menu-item" onClick={() => { setDescPath(p); setActiveDropdown(null); }}>{p}</div>)}
                            </div>
                        )}
                    </div>
                </div>
            </>}

            {/* Save button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button
                    onClick={onClose}
                    style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', color: '#64748b' }}
                >Cancel</button>
                <button
                    onClick={handleSave}
                    style={{ padding: '8px 24px', fontSize: '13px', borderRadius: '8px', border: 'none', background: 'var(--primary-bg-color)', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                >Save</button>
            </div>

            <style>{`
                .path-menu {
                    position: absolute; top: 100%; left: 0; right: 0;
                    background: white; border-radius: 8px; z-index: 100;
                    max-height: 140px; overflow-y: auto; margin-top: 4px;
                }
                .path-menu-item {
                    padding: 8px 12px; font-size: 11px; color: #475569;
                    cursor: pointer; border-bottom: 1px solid #f1f5f9;
                }
                .path-menu-item:hover { background: #f8fafc; color: #0ea5e9; }
            `}</style>
        </div>
    );
};

export default MultiProductDialog;
